import React, { useEffect } from 'react';
import { StyleSheetManager, ThemeProvider } from 'styled-components';
import { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { Cookies } from 'react-cookie';
import { SWRConfig } from 'swr';
import { ChatIframer } from '@belong/chat-utils';
import { getConfig as nextConfig } from '@belong/configs/next/config';
import { globalSWRConfig } from '@belong/configs/swr/globalSWRConfig';
import { CMSConfigProvider, ICMSConfig } from '@belong/contentful';
import { getNativeAppCookie } from '@belong/cookies';
import { getCurrentEnvironment, initPlatformConfig, isBrowser, logAppInfo } from '@belong/env';
import { FeaturesManager, FeatureTogglesContext } from '@belong/feature-toggles';
import { GlobalErrorHandler } from '@belong/logging';
import FeatureProviders from '@belong/providers';
import { useBlueGreen } from '@belong/react-hooks';
import { SearchProvider } from '@belong/search';
import { getTheme, GlobalStyles, isValidProp } from '@belong/themes';
import { Entry, ENVS } from '@belong/types';
import CanonicalLink from '@belong/ui-components/components/global/CanonicalLink';
import { PlatformScript, PolyfillScript } from '@belong/ui-components/components/global/Scripts';
import { ViewPortHead } from '@belong/ui-components/components/page/Head';
import { withBasePath, withoutParams } from '@belong/url-utils';
import { JsonLdSchema } from 'src/components/JsonLdSchema';
import { RichTextDictionary } from 'src/contentful/providers/RichTextDictionary';
import { getParsedSection } from 'src/helpers/contentful';
import AnalyticTracker from '../components/AnalyticsTracker';
import LayoutShell from '../components/LayoutShell';
import { routeConfig } from '../helpers/bootstrap';

const Toolbox = dynamic(() => import('@belong/toolbox/src/view'));
const ErrorPage = dynamic(() => import('./_error'));

export const { basePath, analytic } = nextConfig().publicRuntimeConfig;

const setupAxe = () => {
  if (isBrowser() && getCurrentEnvironment() !== ENVS.PRODUCTION) {
    import('react-dom').then(ReactDOM => {
      import('@axe-core/react').then(axe => {
        axe.default(React, ReactDOM, 1000, {});
      });
    });
  }
};

type ICustomAppProps = Partial<AppProps>;

/* This function removes Adobe Target activities from the page sections
 * adobeTargetActivity sections are transformed in the server side
 * and not needed in client side when calling useCMSPage
 * @param {Entry<any>[]} items - The array of items to process.
 * @returns {Entry<any>[]} - The updated array of items with Adobe Target activities removed from sections.
 */

const removeAdobeTargetActivitiesFromSections = (items: Entry<any>[]) => {
  const [firstItem] = items;

  if (!firstItem) {
    return items;
  }

  const newSections = Array.isArray(firstItem.fields?.sections)
    ? firstItem.fields.sections.filter(section => section?.sys?.contentType?.sys?.id !== 'adobeTargetActivity')
    : firstItem.fields?.sections;

  return [
    {
      ...firstItem,
      fields: {
        ...firstItem.fields,
        sections: newSections
      }
    }
  ];
};

const cmsConfig: ICMSConfig = {
  addBasePathToUrl: withBasePath,
  sectionParser: getParsedSection,
  preprocessItems: removeAdobeTargetActivitiesFromSections
};

export default function PublicApp({ Component, pageProps }: ICustomAppProps) {
  const {
    cookie,
    correlationId,
    features,
    footerMenuContent,
    headerMenuContent,
    host,
    pageData,
    richTextDictionary = {},
    searchServerUrl,
    serverState,
    swrInitialCache
  } = pageProps ?? {};
  const allCookies = new Cookies(cookie);
  const { publicRuntimeConfig } = nextConfig();
  useBlueGreen(host);

  useEffect(() => {
    logAppInfo('Public', correlationId).catch(() => null);
    setupAxe();
  }, [correlationId]);

  if (typeof window !== 'undefined') {
    // Initialize FeaturesManager in _app.tsx on for the client side
    FeaturesManager.init(correlationId, features);
  }

  initPlatformConfig(getNativeAppCookie(cookie));

  return (
    <SWRConfig value={{ ...globalSWRConfig, fallback: { ...globalSWRConfig.fallback, ...swrInitialCache } }}>
      <ThemeProvider theme={getTheme()}>
        <StyleSheetManager shouldForwardProp={isValidProp}>
          <ViewPortHead />
          <GlobalErrorHandler appRoutes={routeConfig.routes} />
          <PlatformScript />
          <PolyfillScript scriptName="broadcastchannel.min.js" />
          <PolyfillScript scriptName="inert.min.js" />
          <FeatureTogglesContext.Provider value={features}>
            <FeatureProviders cookies={allCookies} correlationId={correlationId}>
              <SearchProvider searchServerUrl={searchServerUrl} serverState={serverState}>
                <CanonicalLink pageUrl={withBasePath(withoutParams(pageProps.pageUrl))} />
                <AnalyticTracker page={pageProps} {...analytic} />
                <JsonLdSchema data={pageData?.schemaData} />
                <CMSConfigProvider value={cmsConfig}>
                  <GlobalStyles hasBodyScrollLock />
                  <RichTextDictionary dictionary={richTextDictionary}>
                    {!headerMenuContent || !footerMenuContent ? (
                      <ErrorPage statusCode={500} />
                    ) : (
                      <LayoutShell
                        pageAlert={pageData?.alert}
                        shouldHaveLiveChat={!!pageData?.shouldHaveLiveChat}
                        headerMenuContent={headerMenuContent}
                        footerMenuContent={footerMenuContent}
                        breadcrumbs={pageData?.breadcrumbs}
                        isNbnOrderCartReady={pageData?.isNbnOrderCartReady}
                      >
                        {Component && <Component {...pageProps} />}
                        <ChatIframer />
                      </LayoutShell>
                    )}
                  </RichTextDictionary>
                </CMSConfigProvider>
                {getCurrentEnvironment() !== ENVS.PRODUCTION && (
                  <Toolbox pageProps={pageProps} runtimeConfig={publicRuntimeConfig} />
                )}
              </SearchProvider>
            </FeatureProviders>
          </FeatureTogglesContext.Provider>
        </StyleSheetManager>
      </ThemeProvider>
    </SWRConfig>
  );
}
